import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { usePostHog } from 'posthog-js/react';

import { closeModal } from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';
import * as asyncStorage from 'loot-core/platform/server/asyncStorage';

import countries from '../../../countries.json';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from '../../../hooks/useNavigate';
import { AsyncStorageKey, useStorage } from '../../../hooks/useStorage';
import { theme } from '../../../style';
import { Autocomplete } from '../../autocomplete/Autocomplete';
import { ButtonWithLoading } from '../../common/Button';
import { Input } from '../../common/Input';
import { Link } from '../../common/Link';
import { Text } from '../../common/Text';
import { View } from '../../common/View';
import { FormField, FormLabel } from '../../forms';
import { useIsTablet } from '../../../hooks/useIsTablet';
import styles from '../google-sign-in.module.scss';
import { useGoogleLogin } from '@react-oauth/google';

type RemoteSignupFormProps = {
  initialEmail?: string;
  onSignup?: () => void;
  switchToLogin?: () => void;
};

export function RemoteSignupForm({
  initialEmail,
  onSignup,
  switchToLogin,
}: RemoteSignupFormProps) {
  const posthog = usePostHog();
  const isTablet = useIsTablet();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getItem: getUserLocalData } = useStorage(AsyncStorageKey.USER_DATA);
  const { refreshAuthState } = useAuth();

  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');

  useEffect(() => {
    (async () => {
      const signupData = await getUserLocalData();
      if (signupData) {
        if (!email) {
          setEmail(signupData.email || '');
        }
        setName(signupData.name || '');
        setCountry(signupData.country || '');
      }
    })();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();

    if (password.length < 8) {
      setErrorMsg('Your password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg('Password and Confirm password do not match');
      return;
    }

    setLoading(true);

    const signupData = {
      email,
      password,
      country,
      name: name.trim(),
      isMobile: isTablet,
    };

    posthog.capture('Remote signup', {
      Email: signupData.email,
      Name: signupData.name,
      Country: signupData.country,
      'Is mobile': signupData.isMobile,
    });

    posthog.identify(signupData.name, {
      email: signupData.email,
    });

    const res = await send('subscribe-signup', signupData);

    if (res.error === 'duplicate-email') {
      setErrorMsg('This email address is not available at the moment.');
      setLoading(false);
      return;
    }
    await asyncStorage.setItem(AsyncStorageKey.HAS_REMOTE_ACCOUNT, '1');

    await refreshAuthState();
    setLoading(false);
    onSignup?.();
    dispatch(closeModal());
    navigate('/entry-mode');
  }

  const navigateToLogin = () => {
    if (switchToLogin) {
      switchToLogin();
    } else {
      navigate('/login');
    }
  };

  const loginWithGoogle = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: window.location.origin + '/auth-callback/google',
  });

  return (
    <View style={{ overflow: 'auto', padding: 8 }}>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 10,
        }}
        onSubmit={onSubmit}
      >
        <FormField style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <FormLabel title="Your email address" htmlFor="Email" />
            &nbsp;<Text style={{ color: theme.errorText }}> * </Text>
          </View>
          <Input
            type="email"
            name="email"
            value={email}
            required={true}
            onChange={e => {
              setEmail(e.target.value);
            }}
          />
        </FormField>
        <FormField style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <FormLabel title="Password" htmlFor="password" />
            &nbsp;<Text style={{ color: theme.errorText }}> * </Text>
          </View>
          <Input
            value={password}
            type="password"
            name="password"
            required={true}
            onChange={e => {
              setPassword(e.target.value.trim());
            }}
          />
        </FormField>
        <FormField style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <FormLabel title="Confirm password" htmlFor="confirmPassword" />
            &nbsp;<Text style={{ color: theme.errorText }}> * </Text>
          </View>
          <Input
            value={confirmPassword}
            type="password"
            name="confirmPassword"
            required={true}
            onChange={e => {
              setConfirmPassword(e.target.value.trim());
            }}
          />
        </FormField>
        <FormField style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <FormLabel
              title="What you would like us to call you"
              htmlFor="name"
            />
            &nbsp;<Text style={{ color: theme.errorText }}> * </Text>
          </View>
          <Input
            type="text"
            name="name"
            value={name}
            required={true}
            onChange={e => {
              setName(e.target.value);
            }}
          />
        </FormField>
        <FormField style={{ flex: 1 }}>
          <FormLabel title="What country are you in" htmlFor="country" />
          <Autocomplete
            strict
            suggestions={countries.map(country => ({
              id: country.name,
              name: country.name,
            }))}
            inputProps={{
              autoComplete: 'off',
            }}
            onSelect={value => {
              setCountry(value);
            }}
            value={country}
          />
        </FormField>
        {errorMsg && <View style={{ color: theme.errorText }}>{errorMsg}</View>}

        <ButtonWithLoading disabled={loading} type="primary">
          Signup
        </ButtonWithLoading>
      </form>
      <div
        style={{
          margin: '16px 0 0',
          flexDirection: 'row',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        If you already have an account&nbsp;
        <span>
          <Link
            variant="text"
            style={{ color: theme.pageTextLink, whiteSpace: 'nowrap' }}
            onClick={navigateToLogin}
          >
            Sign in
          </Link>
        </span>
      </div>

      <View style={{ marginTop: '8px', padding: '8px' }}>
        <button
          type="button"
          className={styles.loginWithGoogleGoogleBtn}
          onClick={() => loginWithGoogle()}
        >
          Sign up with Google
        </button>
      </View>
    </View>
  );
}
